import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { cached, tracked } from '@glimmer/tracking';
import { isNone, isPresent } from '@ember/utils';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { AirbnbOfficialLinkStatus } from 'uplisting-frontend/models/airbnb-official-link';

export enum DirectBookingTypes {
  enquiry = 'enquiry',
  requestToBook = 'request_to_book',
  instantBook = 'instant_book',
}

export enum PropertyTypes {
  aparthotel = 'Aparthotel',
  apartment = 'Apartment',
  barn = 'Barn',
  bedAndBreakfast = 'Bed and breakfast',
  boat = 'Boat',
  boutique_hotel = 'Boutique hotel',
  bungalow = 'Bungalow',
  bus = 'Bus',
  cabin = 'Cabin',
  camper_or_rv = 'Camper/RV',
  campsite = 'Campsite',
  casa_particular = 'Casa particular (Cuba)',
  castle = 'Castle',
  cave = 'Cave',
  chalet = 'Chalet',
  condominium = 'Condominium',
  cottage = 'Cottage',
  cycladicHouse = 'Cycladic house (Greece)',
  dammuso = 'Dammuso',
  domeHouse = 'Dome house',
  earthHouse = 'Earth house',
  farm_stay = 'Farm stay',
  guest_suite = 'Guest suite',
  guesthouse = 'Guesthouse',
  heritage_hotel = 'Heritage hotel (India)',
  hostel = 'Hostel',
  hotel = 'Hotel',
  house = 'House',
  houseboat = 'Houseboat',
  hut = 'Hut',
  igloo = 'Igloo',
  island = 'Island',
  lighthouse = 'Lighthouse',
  loft = 'Loft',
  minsu = 'Minsu',
  natureLodge = 'Nature lodge',
  pension = 'Pension (South Korea)',
  plane = 'Plane',
  resort = 'Resort',
  ryokan = 'Ryokan',
  servicedApartment = 'Serviced apartment',
  shepherdsHut = "Shepherd's hut (U.K., France)",
  studio = 'Studio',
  tent = 'Tent',
  tinyHouse = 'Tiny house',
  tipi = 'Tipi',
  townhouse = 'Townhouse',
  train = 'Train',
  treehouse = 'Treehouse',
  trullo = 'Trullo (Italy)',
  villa = 'Villa',
  windmill = 'Windmill',
  yurt = 'Yurt',
}

export enum PropertyBedTypes {
  bunkBed = 'bunk_bed',
  childBed = 'child_bed',
  babyCrib = 'baby_crib',
  double = 'double',
  king = 'king',
  queen = 'queen',
  sleepSofa = 'sleep_sofa',
  twinSingle = 'twin_single',
  murphyBed = 'murphy_bed',
}

export enum PropertyBathroomTypes {
  private = 'private',
  public = 'public',
}

export default class PropertyModel extends Model {
  @attr('string') name!: string;
  @attr('string') description!: string;
  @attr('string') nickname!: string;
  @attr('string') currency!: string;
  @attr('string') timeZone!: string;
  @attr('string') directBookingType!: DirectBookingTypes;
  @attr('boolean', { defaultValue: true }) showOnDirectBookingEngine!: boolean;
  @attr('boolean', { defaultValue: true }) showAirbnbLink!: boolean;
  @attr('boolean') importPricesFromAirbnb!: boolean;
  @attr('number') checkOutTime!: number;
  @attr('number') checkInTime!: number;
  @attr('number') order!: number;
  @attr('number') bedrooms!: number;
  @attr('number') bathrooms!: number;
  @attr('number') beds!: number;
  @attr('string') registrationNumber!: string;
  @attr('boolean', { defaultValue: false })
  showRegistrationNumberOnDbe!: boolean;
  @attr('string') type!: PropertyTypes;
  @attr('number') maximumCapacity!: number;
  @attr('array', {
    defaultValue() {
      return [];
    },
  })
  bedTypes!: PropertyBedTypes[];
  @attr('string') bathroomsType!: PropertyBathroomTypes;
  @attr('string') wifiName!: string;
  @attr('string') wifiPassword!: string;

  @belongsTo('ical', {
    inverse: 'property',
    async: false,
  })
  ical!: ModelRegistry['ical'];

  @belongsTo('policy', {
    inverse: 'property',
    async: true,
  })
  policy!: ModelRegistry['policy'];

  @belongsTo('rental-agreement', {
    inverse: 'property',
    async: true,
  })
  rentalAgreement!: ModelRegistry['rental-agreement'];

  @belongsTo('homeaway-link', {
    inverse: 'property',
    async: false,
  })
  homeawayLink?: ModelRegistry['homeaway-link'];

  @belongsTo('base-rate', {
    inverse: 'property',
    async: false,
  })
  baseRate!: ModelRegistry['base-rate'];

  @belongsTo('property-fee', {
    inverse: 'property',
    async: false,
  })
  propertyFee!: ModelRegistry['property-fee'];

  @belongsTo('property-message-tag', {
    inverse: 'property',
    async: false,
  })
  propertyMessageTag!: ModelRegistry['property-message-tag'];

  @belongsTo('management-fee-rule', {
    inverse: 'properties',
    async: false,
  })
  managementFeeRule!: ModelRegistry['management-fee-rule'];

  @belongsTo('stripe-connection', {
    inverse: 'properties',
    async: false,
  })
  stripeConnection!: ModelRegistry['stripe-connection'];

  @belongsTo('property-management-fee-setting', {
    inverse: 'property',
    async: false,
  })
  propertyManagementFeeSetting!: ModelRegistry['property-management-fee-setting'];

  @belongsTo('changeover-setting', { inverse: 'property', async: false })
  changeoverSetting!: ModelRegistry['changeover-setting'];

  @belongsTo('airbnb-official-link', { inverse: 'property', async: false })
  airbnbOfficialLink?: ModelRegistry['airbnb-official-link'];

  @belongsTo('address', { inverse: 'property', async: false })
  address!: ModelRegistry['address'];

  @belongsTo('suitability', { inverse: 'property', async: false })
  suitability!: ModelRegistry['suitability'];

  @belongsTo('booking-restriction', { inverse: 'property', async: false })
  bookingRestriction!: ModelRegistry['booking-restriction'];

  @belongsTo('bulk-custom-property-attribute', { inverse: null, async: false })
  bulkCustomPropertyAttribute!: ModelRegistry['bulk-custom-property-attribute'];

  @belongsTo('booking-dot-com-link', {
    inverse: 'property',
    async: false,
  })
  bookingDotComLink?: ModelRegistry['booking-dot-com-link'];

  @belongsTo('google-ari-link', {
    inverse: 'property',
    async: false,
  })
  googleAriLink?: ModelRegistry['google-ari-link'];

  @hasMany('upsell', {
    inverse: 'properties',
    async: true,
  })
  upsells!: ModelRegistry['upsell'][];

  @hasMany('photo', {
    inverse: 'property',
    async: true,
  })
  photos!: ModelRegistry['photo'][];

  @hasMany('property-tag', {
    inverse: 'properties',
    async: false,
  })
  propertyTags!: ModelRegistry['property-tag'][];

  @hasMany('multi-unit', {
    inverse: 'property',
    async: false,
  })
  multiUnits!: ModelRegistry['multi-unit'][];

  @hasMany('rate', {
    inverse: 'property',
    async: false,
  })
  rates!: ModelRegistry['rate'][];

  @hasMany('booking', { async: false, inverse: 'property' })
  bookings!: ModelRegistry['booking'][];

  @hasMany('unavailability', { async: false, inverse: 'property' })
  unavailabilities!: ModelRegistry['unavailability'][];

  @hasMany('booking-request', { inverse: 'property', async: false })
  bookingRequests!: ModelRegistry['booking-request'][];

  @hasMany('enquiry', { inverse: null, async: false })
  enquiries!: ModelRegistry['enquiry'][];

  @hasMany('property-check-out-task', { async: false, inverse: 'property' })
  propertyCheckOutTasks!: ModelRegistry['property-check-out-task'][];

  @hasMany('amenity', { async: false, inverse: null })
  amenities!: ModelRegistry['amenity'][];

  @hasMany('external-ical', { async: false, inverse: 'property' })
  externalIcals!: ModelRegistry['external-ical'][];

  @hasMany('property-discount-rule', { async: false, inverse: null })
  propertyDiscountRules!: ModelRegistry['property-discount-rule'][];

  @hasMany('channel-listing', { async: false, inverse: 'property' })
  channelListings!: ModelRegistry['channel-listing'][];

  @hasMany('channel-promotion', { async: false, inverse: 'property' })
  channelPromotions!: ModelRegistry['channel-promotion'][];

  // correctly keep track when property airbnb official link was moved to another property
  @tracked wasAirbnbLinkReplaced = false;

  @tracked failedPhoto?: string;

  @cached
  get searchName(): string {
    return this.nickname;
  }

  @cached
  get hasFeeRule(): boolean {
    return !!this.managementFeeRule?.id;
  }

  @cached
  get forLockable(): string {
    return this.nickname;
  }

  @cached
  get instantBookable(): boolean {
    return this.directBookingType === DirectBookingTypes.instantBook;
  }

  @cached
  get hasConfirmedAirbnbLink(): boolean {
    return (
      this.airbnbOfficialLink?.status === AirbnbOfficialLinkStatus.confirmed
    );
  }

  @cached
  get hasReadOnlyAirbnbLink(): boolean {
    return !!this.airbnbOfficialLink?.isReadOnly;
  }

  @cached
  get hasConnectedBookingDotComLink(): boolean {
    return this.bookingDotComLink?.isConnected ?? false;
  }

  @cached
  get hasConnectedGoogleLink(): boolean {
    return !!this.googleAriLink?.id;
  }

  @cached
  get hasConnectedVrboLink(): boolean {
    return !!this.homeawayLink?.id;
  }

  @cached
  get hasAccommodationInfo(): boolean {
    return ['bedrooms', 'beds', 'bathrooms'].every((key) => !isNone(this[key]));
  }

  @cached
  get lastMinuteDiscountRules(): ModelRegistry['property-discount-rule'][] {
    return this.propertyDiscountRules.filter(
      (rule) => rule.isLastMinuteDiscountRule,
    );
  }

  @cached
  get earlyBirdDiscountRules(): ModelRegistry['property-discount-rule'][] {
    return this.propertyDiscountRules.filter(
      (rule) => rule.isEarlyBirdDiscountRule,
    );
  }

  @cached
  get photoPreview(): string | undefined {
    if (this.isDestroyed || this.isDestroying) {
      return undefined;
    }

    const ids = (this as PropertyModel).hasMany('photos').ids();

    const photos = ids
      .map((id) => this.store.peekRecord('photo', id))
      .filter(Boolean);

    const preview = photos.find((photo) => isPresent(photo.preview))?.preview;

    return preview === this.failedPhoto ? undefined : preview;
  }
}

// read from the image, as the row's and the menu's can fail at once
export function markPhotoFailed(property: PropertyModel, event: Event): void {
  property.failedPhoto =
    (event.target as HTMLImageElement).getAttribute('src') ?? undefined;
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    property: PropertyModel;
  }
}
